import { Check, FileText, LoaderCircle, TriangleAlert } from 'lucide-react';
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';

import '../workspace.css';

export interface PdfPagePreviewItem {
  readonly id: string;
  readonly pageNumber: number;
  readonly width?: number;
  readonly height?: number;
  readonly rotation?: number;
  readonly disabled?: boolean;
}

export interface PdfThumbnailRenderContext {
  readonly page: PdfPagePreviewItem;
  readonly canvas: HTMLCanvasElement;
  readonly targetWidth: number;
  readonly pixelRatio: number;
  readonly signal: AbortSignal;
}

export type PdfThumbnailRenderer = (
  context: PdfThumbnailRenderContext,
) => void | Promise<void>;

export interface PdfPagePreviewProps {
  readonly pages: readonly PdfPagePreviewItem[];
  readonly selectedPageIds: readonly string[];
  readonly renderThumbnail: PdfThumbnailRenderer;
  readonly onTogglePage: (pageId: string, selected: boolean) => void;
  readonly activePageId?: string;
  readonly onActivatePage?: (pageId: string) => void;
  readonly onSelectAll?: () => void;
  readonly onSelectNone?: () => void;
  readonly disabled?: boolean;
  readonly targetWidth?: number;
  readonly rootMargin?: string;
  readonly className?: string;
}

interface LazyPageProps {
  readonly page: PdfPagePreviewItem;
  readonly selected: boolean;
  readonly active: boolean;
  readonly disabled: boolean;
  readonly targetWidth: number;
  readonly rootMargin: string;
  readonly scrollRootRef: RefObject<HTMLDivElement | null>;
  readonly renderThumbnail: PdfThumbnailRenderer;
  readonly onToggle: (selected: boolean) => void;
  readonly onActivate?: () => void;
}

type ThumbnailStatus = 'waiting' | 'rendering' | 'ready' | 'error';

function LazyPage({
  page,
  selected,
  active,
  disabled,
  targetWidth,
  rootMargin,
  scrollRootRef,
  renderThumbnail,
  onToggle,
  onActivate,
}: LazyPageProps) {
  const { t } = useTranslation();
  const errorId = useId();
  const itemRef = useRef<HTMLLIElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<ThumbnailStatus>('waiting');
  const baseRatio = page.width && page.height && page.width > 0 && page.height > 0
    ? page.width / page.height
    : 0.707;
  const normalizedRotation = ((page.rotation ?? 0) % 360 + 360) % 360;
  const ratio = normalizedRotation === 90 || normalizedRotation === 270
    ? 1 / baseRatio
    : baseRatio;
  const style = { '--workspace-page-ratio': String(ratio) } as CSSProperties;

  useEffect(() => {
    const element = itemRef.current;
    if (!element) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some(({ isIntersecting }) => isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { root: scrollRootRef.current, rootMargin },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [rootMargin, scrollRootRef]);

  useEffect(() => {
    if (!visible || !canvasRef.current) return undefined;

    const controller = new AbortController();
    const canvas = canvasRef.current;
    setStatus('rendering');
    const pixelRatio = typeof window === 'undefined'
      ? 1
      : Math.min(Math.max(window.devicePixelRatio || 1, 1), 2);

    void Promise.resolve(renderThumbnail({
      page,
      canvas,
      targetWidth,
      pixelRatio,
      signal: controller.signal,
    })).then(
      () => {
        if (!controller.signal.aborted) setStatus('ready');
      },
      () => {
        if (!controller.signal.aborted) setStatus('error');
      },
    );

    return () => {
      controller.abort();
      canvas.width = 0;
      canvas.height = 0;
    };
  }, [page, renderThumbnail, targetWidth, visible]);

  const pageLabel = selected
    ? t('workflow.pages.deselectPage', { page: page.pageNumber })
    : t('workflow.pages.selectPage', { page: page.pageNumber });

  return (
    <li ref={itemRef} className="workspace-page" style={style} data-active={active || undefined}>
      <button
        className="workspace-page__button"
        type="button"
        dir="ltr"
        disabled={disabled || page.disabled}
        aria-label={pageLabel}
        aria-describedby={status === 'error' ? errorId : undefined}
        aria-pressed={selected}
        onClick={() => {
          onActivate?.();
          onToggle(!selected);
        }}
      >
        <span className="workspace-page__paper">
          <canvas
            ref={canvasRef}
            className="workspace-page__canvas"
            aria-hidden="true"
            data-ready={status === 'ready' || undefined}
          />
          {status !== 'ready' && (
            <span className="workspace-page__placeholder" data-status={status} aria-hidden="true">
              {status === 'rendering' && <LoaderCircle className="workspace-spin" size={24} />}
              {status === 'waiting' && <FileText size={25} />}
              {status === 'error' && <TriangleAlert size={24} />}
            </span>
          )}
          {selected && (
            <span className="workspace-page__selected" aria-hidden="true">
              <Check size={15} strokeWidth={3} />
            </span>
          )}
        </span>
        <span className="workspace-page__caption">
          <span>{page.pageNumber}</span>
          {page.rotation !== undefined && page.rotation % 360 !== 0 && (
            <small>{page.rotation}°</small>
          )}
          {status === 'error' && (
            <small id={errorId} className="workspace-page__error" dir="auto">{t('errors.processing.failed')}</small>
          )}
        </span>
      </button>
    </li>
  );
}

export function PdfPagePreview({
  pages,
  selectedPageIds,
  renderThumbnail,
  onTogglePage,
  activePageId,
  onActivatePage,
  onSelectAll,
  onSelectNone,
  disabled = false,
  targetWidth = 220,
  rootMargin = '320px',
  className,
}: PdfPagePreviewProps) {
  const { t } = useTranslation();
  const scrollRootRef = useRef<HTMLDivElement>(null);
  const selected = useMemo(() => new Set(selectedPageIds), [selectedPageIds]);
  const classes = ['workspace-page-preview', className].filter(Boolean).join(' ');

  return (
    <section className={classes} aria-label={t('a11y.thumbnailList')}>
      <div className="workspace-section-heading">
        <h2>{t('workflow.pages.title')}</h2>
        {(onSelectAll || onSelectNone) && (
          <span className="workspace-page-preview__bulk">
            {onSelectAll && (
              <button type="button" disabled={disabled || selected.size === pages.length} onClick={onSelectAll}>
                {t('common.actions.selectAll')}
              </button>
            )}
            {onSelectNone && (
              <button type="button" disabled={disabled || selected.size === 0} onClick={onSelectNone}>
                {t('common.actions.selectNone')}
              </button>
            )}
          </span>
        )}
      </div>
      <div ref={scrollRootRef} className="workspace-page-preview__scroller" dir="ltr">
        <ol className="workspace-page-grid" dir="ltr">
          {pages.map((page) => (
            <LazyPage
              key={page.id}
              page={page}
              selected={selected.has(page.id)}
              active={activePageId === page.id}
              disabled={disabled}
              targetWidth={targetWidth}
              rootMargin={rootMargin}
              scrollRootRef={scrollRootRef}
              renderThumbnail={renderThumbnail}
              onToggle={(nextSelected) => onTogglePage(page.id, nextSelected)}
              onActivate={onActivatePage ? () => onActivatePage(page.id) : undefined}
            />
          ))}
        </ol>
      </div>
    </section>
  );
}
